import React, { useState } from 'react';
import { Breadcrumbs, IconButton, Link, Menu, MenuItem, Tooltip } from '@mui/material';
import { CreateNewFolder, Folder, Home, MoreVert } from '@mui/icons-material';

import FileFolderModel, { flattenFolders } from '$fbusiness/models/fileFolder';
import { Button } from '$gcomponents/primitives';

interface FolderBarProps {
  folders: Array<FileFolderModel>;
  trail: Array<FileFolderModel>;
  current: FileFolderModel | null;
  maxDepth: number;
  canEdit: boolean;
  onOpen: (folderId: number | null) => void;
  onCreate: (parentId: number | null) => void;
  onRename: (folder: FileFolderModel) => void;
  onDelete: (folder: FileFolderModel) => void;
}

/**
 * Breadcrumbs + the subfolders of wherever you currently are.
 *
 * Folders live in the Files screen rather than a settings page: people make them
 * while filing, not in advance.
 */
const FolderBar: React.FC<FolderBarProps> = ({
  folders,
  trail,
  current,
  maxDepth,
  canEdit,
  onOpen,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [menuFor, setMenuFor] = useState<FileFolderModel | null>(null);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const visible = current ? current.children : folders;
  // Depth is 1-based, so a folder at maxDepth can hold files but not folders.
  const canNest = !current || current.depth < maxDepth;

  const openMenu = (e: React.MouseEvent<HTMLElement>, folder: FileFolderModel) => {
    e.stopPropagation();
    setAnchor(e.currentTarget);
    setMenuFor(folder);
  };
  const closeMenu = () => {
    setAnchor(null);
    setMenuFor(null);
  };

  return (
    <div className="folder-bar">
      <div className="folder-crumbs">
        <Breadcrumbs aria-label="folder path">
          <Link
            component="button"
            underline={current ? 'hover' : 'none'}
            color={current ? 'inherit' : 'text.primary'}
            onClick={() => onOpen(null)}>
            <Home fontSize="small" className="crumb-icon" />
            All files
          </Link>
          {trail.map((f, i) => {
            const isLast = i === trail.length - 1;
            return (
              <Link
                key={f.id}
                component="button"
                underline={isLast ? 'none' : 'hover'}
                color={isLast ? 'text.primary' : 'inherit'}
                onClick={() => onOpen(f.id)}>
                {f.name}
              </Link>
            );
          })}
        </Breadcrumbs>

        {canEdit && (
          <Tooltip
            title={canNest ? 'New folder here' : `Folders only go ${maxDepth} levels deep`}>
            <span>
              <Button
                size="small"
                color="secondary"
                disabled={!canNest}
                onClick={() => onCreate(current ? current.id : null)}>
                <CreateNewFolder fontSize="small" className="crumb-icon" />
                New folder
              </Button>
            </span>
          </Tooltip>
        )}
      </div>

      {visible.length > 0 && (
        <div className="folder-grid">
          {visible.map((f) => (
            <div
              key={f.id}
              className="folder-chip"
              role="button"
              tabIndex={0}
              onClick={() => onOpen(f.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') onOpen(f.id);
              }}>
              <Folder fontSize="small" className="folder-chip-icon" />
              <span className="folder-chip-name" title={f.name}>
                {f.name}
              </span>
              {canEdit && (
                <IconButton size="small" onClick={(e) => openMenu(e, f)} aria-label="Folder actions">
                  <MoreVert fontSize="small" />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      )}

      <Menu anchorEl={anchor} open={!!menuFor} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            if (menuFor) onRename(menuFor);
            closeMenu();
          }}>
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuFor) onDelete(menuFor);
            closeMenu();
          }}>
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
};

export const folderSelectOptions = (folders: Array<FileFolderModel>, excludeId?: number) =>
  flattenFolders(folders)
    // When re-filing a folder's contents, its own subtree is not a valid target.
    .filter(({ folder }) => !excludeId || folder.id !== excludeId)
    .map(({ folder, depth }) => ({
      value: folder.id,
      labelText: `${'  '.repeat(depth)}${depth ? '└ ' : ''}${folder.name}`,
    }));

export default FolderBar;
